import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useAuthState } from '../hooks/useAuthState';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateEmail, updatePassword, sendPasswordResetEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { getAdminIds, isAdmin, addAdminByPhone, removeAdmin } from '../lib/firestore/admins';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../styles/UserSettings.css';

const UserSettings = () => {
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    smsPreference: 'all', // 'all' | 'friends_only' | 'none'
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [resetEmailSent, setResetEmailSent] = useState(false);

  const [isAdminUser, setIsAdminUser] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [adminPhoneInput, setAdminPhoneInput] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');

  useEffect(() => {
    // Wait for auth to finish loading first!
    if (authLoading) {
      return; // Don't do anything while auth is loading
    }
    
    console.log('UserSettings: User state:', user);
    if (!user) {
      console.log('UserSettings: No user, redirecting to login');
      navigate('/login');
      return;
    }
    console.log('UserSettings: Loading user data');
    loadUserData();
    loadAdminSection();
  }, [user, authLoading, navigate]);

  const loadAdminSection = async () => {
    if (!user) return;
    try {
      const ok = await isAdmin(user.uid);
      setIsAdminUser(ok);
      if (ok) {
        const ids = await getAdminIds();
        const withNames = await Promise.all(
          ids.map(async (userId) => {
            try {
              const snap = await getDoc(doc(db, 'users', userId));
              const d = snap.data();
              const name = [d?.firstName, d?.lastName].filter(Boolean).join(' ') || 'Unknown';
              return { userId, displayName: name };
            } catch {
              return { userId, displayName: userId };
            }
          })
        );
        setAdminList(withNames);
      }
    } catch {
      setIsAdminUser(false);
    }
  };

  const handleAddAdminByPhone = async (e) => {
    e.preventDefault();
    if (!user || !adminPhoneInput.trim()) return;
    setAddingAdmin(true);
    setAdminError('');
    setAdminSuccess('');
    try {
      const result = await addAdminByPhone(adminPhoneInput.trim(), user.uid);
      if (result.success) {
        setAdminPhoneInput('');
        setAdminSuccess('Admin added.');
        setTimeout(() => setAdminSuccess(''), 3000);
        await loadAdminSection();
      } else {
        setAdminError(result.error || 'Failed to add');
      }
    } catch (err) {
      setAdminError(err.message || 'Failed to add admin');
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!user || !confirm('Remove this admin?')) return;
    try {
      await removeAdmin(userId);
      setAdminSuccess('Admin removed.');
      setTimeout(() => setAdminSuccess(''), 3000);
      await loadAdminSection();
    } catch (err) {
      setAdminError(err.message || 'Failed to remove');
    }
  };

  const loadUserData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Check if user.email is a phone-based account (ends with @hotelia.local)
        // If so, use Firestore email instead. Otherwise, use Firestore email if available, fallback to auth email
        const isPhoneBasedAccount = user.email && user.email.endsWith('@hotelia.local');
        const email = isPhoneBasedAccount 
          ? (data.email || '') // Phone-based account: only use Firestore email
          : (data.email || user.email || ''); // Regular account: prefer Firestore, fallback to auth
        
        setUserData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: email,
          phone: data.phone || data.phoneNumber || '', // Always use Firestore phone
          smsPreference: data.smsPreference || (data.smsConsent === false ? 'none' : 'all'),
        });
      } else {
        // No Firestore doc yet - check if it's a phone-based account
        const isPhoneBasedAccount = user.email && user.email.endsWith('@hotelia.local');
        setUserData({
          firstName: '',
          lastName: '',
          email: isPhoneBasedAccount ? '' : (user.email || ''), // Don't show phone-based email
          phone: '', // Will be populated from Firestore when doc is created
        });
      }
    } catch (err) {
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Update Firestore user document
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: userData.firstName,
        lastName: userData.lastName,
        updatedAt: new Date(),
      });

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Only update Firebase Auth email if a real email is provided (not phone-based)
      // For phone-based accounts, we only store email in Firestore
      const isPhoneBasedAccount = user.email && user.email.endsWith('@hotelia.local');
      
      if (userData.email && !isPhoneBasedAccount) {
        // Only update Firebase Auth email for non-phone accounts
        try {
          await updateEmail(user, userData.email);
        } catch (authErr) {
          // If auth update fails (e.g., requires recent login), just update Firestore
          if (authErr.code !== 'auth/requires-recent-login') {
            throw authErr;
          }
        }
      }
      
      // Always update Firestore (this is the source of truth)
      await updateDoc(doc(db, 'users', user.uid), {
        email: userData.email || null, // Store null if empty (optional field)
        updatedAt: new Date(),
      });

      setSuccess('Email updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setError('Email saved to profile. To update Firebase Auth email, please re-authenticate by signing out and back in.');
        // Still show success since Firestore was updated
        setSuccess('Email saved to your profile!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(err.message || 'Failed to update email');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    if (!user || !userData.phone) {
      setError('Phone number is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Normalize phone to E.164 format
      const phoneE164 = userData.phone.startsWith('+') 
        ? userData.phone 
        : `+1${userData.phone.replace(/\D/g, '')}`;
      
      // Update Firestore (this is the source of truth)
      await updateDoc(doc(db, 'users', user.uid), {
        phone: phoneE164,
        phoneNumber: phoneE164.replace(/\D/g, ''), // Also store digits-only version
        updatedAt: new Date(),
      });

      setSuccess('Phone number updated in profile. Note: Changing your login phone number requires creating a new account.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to update phone number');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!user) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Re-authenticate first
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.newPassword);

      setSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (err.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Please re-authenticate. Try signing out and back in.');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      setError('No email address found for password reset');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetEmailSent(true);
      setSuccess('Password reset email sent! Check your inbox.');
      setTimeout(() => {
        setResetEmailSent(false);
        setSuccess('');
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to send password reset email');
    }
  };

  const handleUpdateSMSPreferences = async (e) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        smsPreference: userData.smsPreference,
        updatedAt: new Date(),
      };

      // If opting out completely, also set smsConsent to false
      if (userData.smsPreference === 'none') {
        updateData.smsConsent = false;
        updateData.smsOptOutDate = new Date();
      } else {
        // If opting back in, set smsConsent to true
        updateData.smsConsent = true;
        // Clear opt-out date if they're opting back in
        updateData.smsOptOutDate = null;
      }

      await updateDoc(doc(db, 'users', user.uid), updateData);

      setSuccess('SMS preferences updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update SMS preferences');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="settings-container">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  console.log('UserSettings: Rendering settings page');

  return (
    <div className="settings-container">
      <div className="settings-card">
        <header className="settings-header">
          <button className="btn-back" onClick={() => navigate('/home')}>
            ← Back
          </button>
          <h1>Settings</h1>
          <p>Manage your account and preferences</p>
        </header>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="settings-sections">
          {/* Profile Information */}
          <section className="settings-section">
            <h2>Profile Information</h2>
            <form onSubmit={handleUpdateProfile} className="settings-form">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  className="input"
                  value={userData.firstName}
                  onChange={(e) =>
                    setUserData({ ...userData, firstName: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  className="input"
                  value={userData.lastName}
                  onChange={(e) =>
                    setUserData({ ...userData, lastName: e.target.value })
                  }
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update Profile'}
              </button>
            </form>
          </section>

          {/* Email Settings */}
          <section className="settings-section">
            <h2>Email Address (Optional)</h2>
            <form onSubmit={handleUpdateEmail} className="settings-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  className="input"
                  value={userData.email}
                  onChange={(e) =>
                    setUserData({ ...userData, email: e.target.value })
                  }
                  placeholder="Enter your email (optional)"
                />
                <small className="form-hint">
                  Email is optional. Used for password recovery and notifications.
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Updating...' : 'Update Email'}
              </button>
            </form>
          </section>

          {/* Phone Number Settings */}
          <section className="settings-section">
            <h2>Phone Number</h2>
            <form onSubmit={handleUpdatePhone} className="settings-form">
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={userData.phone}
                  onChange={(value) =>
                    setUserData({ ...userData, phone: value || '' })
                  }
                  className="phone-input"
                  id="phone"
                  required
                />
                <small className="form-hint">
                  Your phone number is required for account access. Changing it requires re-verification.
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !userData.phone}
              >
                {saving ? 'Updating...' : 'Update Phone'}
              </button>
            </form>
          </section>

          {/* Password Change */}
          <section className="settings-section">
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword} className="settings-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  className="input"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  className="input"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                  required
                  minLength={6}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="input"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </section>

          {/* Password Reset */}
          <section className="settings-section">
            <h2>Password Recovery</h2>
            <div className="password-reset-section">
              <p>Forgot your password? We can send a reset link to your email.</p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePasswordReset}
                disabled={resetEmailSent || !user?.email}
              >
                {resetEmailSent ? 'Email Sent ✓' : 'Send Password Reset Email'}
              </button>
              {resetEmailSent && (
                <p className="reset-hint">
                  Check your inbox for instructions to reset your password.
                </p>
              )}
            </div>
          </section>

          {/* SMS Preferences */}
          <section className="settings-section">
            <h2>Text Message Preferences</h2>
            <form onSubmit={handleUpdateSMSPreferences} className="settings-form">
              <div className="form-group">
                <label>When should you receive text messages?</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="smsPreference"
                      value="all"
                      checked={userData.smsPreference === 'all'}
                      onChange={(e) =>
                        setUserData({ ...userData, smsPreference: e.target.value })
                      }
                    />
                    <span>
                      <strong>Receive all text messages</strong>
                      <small>Get event invites, reminders, and notifications from anyone</small>
                    </span>
                  </label>

                  <label className="radio-label">
                    <input
                      type="radio"
                      name="smsPreference"
                      value="friends_only"
                      checked={userData.smsPreference === 'friends_only'}
                      onChange={(e) =>
                        setUserData({ ...userData, smsPreference: e.target.value })
                      }
                    />
                    <span>
                      <strong>Only from friends</strong>
                      <small>Only receive messages from people you're friends with</small>
                    </span>
                  </label>

                  <label className="radio-label">
                    <input
                      type="radio"
                      name="smsPreference"
                      value="none"
                      checked={userData.smsPreference === 'none'}
                      onChange={(e) =>
                        setUserData({ ...userData, smsPreference: e.target.value })
                      }
                    />
                    <span>
                      <strong>Don't receive any text messages</strong>
                      <small>Opt out of all SMS notifications. You'll still get in-app notifications.</small>
                    </span>
                  </label>
                </div>
                <small className="form-hint">
                  You can also opt-out by replying <strong>STOP</strong> to any message. 
                  Message and data rates may apply.
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update SMS Preferences'}
              </button>
            </form>
          </section>

          {/* Manage Admins - only visible to admins */}
          {isAdminUser && (
            <section className="settings-section">
              <h2>Manage Admins</h2>
              <p className="form-hint">Admins can edit and delete any event or group. Add by phone number (user must have an account with that phone).</p>
              {adminError && <div className="error-message">{adminError}</div>}
              {adminSuccess && <div className="success-message">{adminSuccess}</div>}
              <form onSubmit={handleAddAdminByPhone} className="settings-form" style={{ marginBottom: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="adminPhone">Add admin by phone number</label>
                  <input
                    type="tel"
                    id="adminPhone"
                    className="input"
                    placeholder="+1 234 567 8900"
                    value={adminPhoneInput}
                    onChange={(e) => setAdminPhoneInput(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={addingAdmin}>
                  {addingAdmin ? 'Adding...' : 'Add admin'}
                </button>
              </form>
              <div>
                <strong>Current admins:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                  {adminList.map(({ userId, displayName }) => (
                    <li key={userId} style={{ marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{displayName}</span>
                      {userId !== user?.uid && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                          onClick={() => handleRemoveAdmin(userId)}
                        >
                          Remove
                        </button>
                      )}
                      {userId === user?.uid && <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>(you)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Sign Out */}
          <section className="settings-section">
            <h2>Sign Out</h2>
            <div className="password-reset-section">
              <p>Sign out of your account</p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  await signOut(auth);
                  navigate('/login');
                }}
              >
                Sign Out
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;


